<script>
  import { onMount } from "svelte";
  import { Chart } from "chart.js";
  import Modal from "../../components/Modal.svelte";
  import { makeApiRequest } from "../../utils/ajax";

  const postsToShow = 4;

  let { posts, stillScraping } = $props();

  let viewAllPosts = $state(false);
  let selectedPost = $state(null);
  let analyticsData = $state(null);
  let showAnalyticsModal = $state(false);
  let loading = $state(false);
  let analyticsChart = $state(null);

  function togglePosts(e) {
    e.preventDefault();
    viewAllPosts = !viewAllPosts;
  }

  async function openPostAnalytics(post, event) {
    event.preventDefault();
    event.stopPropagation();

    selectedPost = post;
    loading = true;
    showAnalyticsModal = true;
    analyticsData = null;

    try {
      const data = await makeApiRequest(`/api/posts/${post.id}/analytics`);
      analyticsData = data;

      // Setup chart after modal is visible
      setTimeout(() => setupAnalyticsChart(), 100);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      loading = false;
    }
  }

  function closeAnalyticsModal() {
    showAnalyticsModal = false;
    selectedPost = null;
    analyticsData = null;
    if (analyticsChart) {
      analyticsChart.destroy();
      analyticsChart = null;
    }
  }

  function setupAnalyticsChart() {
    if (!analyticsData?.analytics?.timeline || analyticsChart) return;

    const canvas = document.getElementById("analyticsChart");
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const timeline = analyticsData.analytics.timeline;

    // Format dates for better readability
    const formattedLabels = formatChartLabels(
      timeline.map((t) => new Date(t.date))
    );

    analyticsChart = new Chart(ctx, {
      type: "line",
      data: {
        labels: formattedLabels,
        datasets: [
          {
            label: "Likes",
            data: timeline.map((t) => t.likes_count),
            borderColor: "rgb(239, 68, 68)",
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            tension: 0.4,
            fill: true,
          },
          {
            label: "Comments",
            data: timeline.map((t) => t.comments_count),
            borderColor: "rgb(59, 130, 246)",
            backgroundColor: "rgba(59, 130, 246, 0.1)",
            tension: 0.4,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
          },
          title: {
            display: true,
            text: "Engagement Over Time",
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: "rgba(0, 0, 0, 0.1)",
            },
          },
          x: {
            grid: {
              color: "rgba(0, 0, 0, 0.1)",
            },
            title: {
              display: true,
              text: "Date & Time",
            },
            ticks: {
              maxRotation: 45,
              font: {
                size: 11,
              },
              color: function (context) {
                // Highlight date labels (those with calendar emoji) in a different color
                const label = context.tick.label;
                return label && label.includes("📅") ? "#4F46E5" : "#6B7280";
              },
              callback: function (value, index) {
                const label = this.getLabelForValue(value);
                return label;
              },
            },
          },
        },
      },
    });
  }

  function formatChartLabels(dates) {
    if (!dates || dates.length === 0) return [];

    const firstDate = dates[0];
    const lastDate = dates[dates.length - 1];
    const daysDifference = (lastDate - firstDate) / (1000 * 60 * 60 * 24);

    let previousDay = null;
    let previousMonth = null;

    return dates.map((date, index) => {
      const currentDay = date.toDateString(); // Full day string for comparison
      const currentMonth = `${date.getMonth()}-${date.getFullYear()}`;

      if (daysDifference < 1) {
        // Less than a day - show time only
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      } else if (daysDifference <= 7) {
        // Up to a week - show day and time, highlight date changes
        const timeString = date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });

        // Show full date + time for first point or when day changes
        if (index === 0 || currentDay !== previousDay) {
          previousDay = currentDay;
          const dateString = date.toLocaleDateString([], {
            month: "short",
            day: "numeric",
          });
          return `📅 ${dateString} ${timeString}`;
        } else {
          // Same day, show only time
          return timeString;
        }
      } else if (daysDifference <= 30) {
        // Up to a month - show date only when it changes, make it prominent
        if (index === 0 || currentDay !== previousDay) {
          previousDay = currentDay;
          const dateString = date.toLocaleDateString([], {
            month: "short",
            day: "numeric",
          });
          return `📅 ${dateString}`;
        } else {
          // Same date, show tick mark
          return "│";
        }
      } else {
        // More than a month - show month/year only when it changes
        if (index === 0 || currentMonth !== previousMonth) {
          previousMonth = currentMonth;
          const dateString = date.toLocaleDateString([], {
            month: "short",
            year: "numeric",
          });
          return `📅 ${dateString}`;
        } else {
          // Same month, show tick mark
          return "│";
        }
      }
    });
  }

  function formatNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num?.toLocaleString() || "0";
  }

  function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
</script>

<div class="bg-white shadow rounded-lg p-4 sm:p-6">
  <div class="flex justify-between items-center mb-4">
    <h3 class="text-lg font-medium text-gray-900">
      Recent Posts
      <span class="text-xs text-gray-500 ml-2 hidden sm:inline"
        >(click post to see analytics)</span
      >
    </h3>

    {#if posts.length > postsToShow}
      <a
        onclick={togglePosts}
        href="#"
        class="text-sm font-medium text-indigo-600 hover:text-indigo-800"
        >View {viewAllPosts ? "Less" : "More"}</a
      >
    {/if}
  </div>

  {#if stillScraping && posts.length === 0}
    <div
      class="flex flex-col items-center justify-center py-6 bg-gray-50 rounded-lg shadow-sm"
    >
      <svg
        class="w-10 h-10 text-indigo-400 mb-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
        ></path>
      </svg>
      <p class="text-gray-600 font-medium mb-2">Loading recent posts</p>
      <div class="flex space-x-1 mt-1">
        <div
          class="w-2 h-2 bg-indigo-500 rounded-full animate-bounce"
          style="animation-delay: 0ms"
        ></div>
        <div
          class="w-2 h-2 bg-indigo-500 rounded-full animate-bounce"
          style="animation-delay: 150ms"
        ></div>
        <div
          class="w-2 h-2 bg-indigo-500 rounded-full animate-bounce"
          style="animation-delay: 300ms"
        ></div>
      </div>
    </div>
  {/if}

  <div class="space-y-3 sm:space-y-4">
    {#each posts.slice(0, viewAllPosts ? 999 : postsToShow) as post}
      <div
        class="bg-gray-50 rounded-lg overflow-hidden shadow hover:shadow-md transition-all duration-300 flex min-h-24 sm:min-h-28 cursor-pointer hover:bg-gray-100"
        onclick={(e) => openPostAnalytics(post, e)}
      >
        <div
          class="w-1/4 sm:w-1/5 bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center overflow-hidden"
        >
          <div class="p-0 h-full w-full">
            {#if post.image_url}
              <img
                src={post.image_url}
                alt="Post Image"
                class="w-full h-full object-cover"
              />
            {/if}
          </div>
        </div>
        <div class="w-3/4 sm:w-4/5 p-3 sm:p-4 flex flex-col">
          <div class="flex justify-between items-start mb-2">
            <div class="flex items-center space-x-2 sm:space-x-3">
              <span class="text-xs font-medium text-gray-800"
                >{formatNumber(post.likes_count || 0)} ❤️</span
              >
              <span class="text-xs font-medium text-gray-800"
                >{formatNumber(post.comments_count || 0)} 💬</span
              >
            </div>
            <div class="flex items-center">
              <span class="text-xs text-gray-500 mr-2 sm:mr-3">
                {new Date(post.taken_at_timestamp * 1000).toLocaleDateString(
                  "en-US",
                  {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  }
                )}
              </span>
              <div class="flex items-center space-x-1 sm:space-x-2">
                <span
                  class="text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 bg-indigo-100 text-indigo-700 rounded-full font-medium"
                >
                  📊 Analytics
                </span>
                <a
                  href={`https://instagram.com/p/${post.shortcode}`}
                  target="_blank"
                  onclick={(e) => e.stopPropagation()}
                  class="text-indigo-600 hover:text-indigo-800"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4 sm:h-5 sm:w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <p class="text-xs text-gray-600 line-clamp-2 mb-2 flex-grow">
            {#if post.caption}
              {post.caption.split("\n")[0]}
            {:else}
              &nbsp
            {/if}
          </p>

          {#if post.engagement_score}
            <div class="mt-auto pt-2 border-t border-gray-100">
              <div class="flex items-center">
                <span class="text-xs font-medium text-gray-500 mr-2"
                  >Engagement:</span
                >
                <div class="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    class="bg-gradient-to-r from-purple-500 to-pink-500 h-1.5 rounded-full"
                    style="width: {post.engagement_score}%"
                  ></div>
                </div>
                <span class="text-xs font-medium text-gray-500 ml-2"
                  >{post.engagement_score}%</span
                >
              </div>
            </div>
          {/if}
        </div>
      </div>
    {/each}
  </div>
</div>

<!-- Analytics Modal -->
<Modal
  open={showAnalyticsModal}
  title={selectedPost
    ? `Post Analytics - ${formatDate(selectedPost.posted_at)}`
    : "Post Analytics"}
  size="6xl"
  closed={closeAnalyticsModal}
>
  {#snippet content()}
    <div class="space-y-6">
      {#if loading}
        <div class="flex justify-center items-center py-12">
          <div
            class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"
          ></div>
          <span class="ml-4 text-lg font-medium text-gray-700"
            >Loading analytics...</span
          >
        </div>
      {:else if analyticsData}
        {@const { analytics } = analyticsData}

        <!-- Post Header -->
        <div
          class="flex items-start space-x-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg"
        >
          {#if selectedPost.image_url}
            <img
              src={selectedPost.image_url}
              alt="Post"
              class="w-20 h-20 rounded-lg object-cover"
            />
          {/if}
          <div class="flex-1">
            <div class="flex items-center space-x-2 mb-2">
              <span class="text-lg font-bold text-gray-900">
                {formatNumber(analytics.current_stats.likes_count)} likes
              </span>
              <span class="text-gray-400">•</span>
              <span class="text-lg font-bold text-gray-900">
                {formatNumber(analytics.current_stats.comments_count)} comments
              </span>
            </div>
            <p class="text-sm text-gray-600 mb-3">
              {#if selectedPost.caption}
                {selectedPost.caption.slice(0, 200)}{selectedPost.caption
                  .length > 200
                  ? "..."
                  : ""}
              {:else}
                No caption
              {/if}
            </p>
            <div class="flex items-center space-x-4 text-sm text-gray-500">
              <span>Posted {analytics.current_stats.age_in_days} days ago</span>
              <span>•</span>
              <span
                >Engagement Rate: {analytics.engagement_breakdown
                  .engagement_rate}%</span
              >
              {#if analytics.current_stats.is_video}
                <span
                  class="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs"
                  >Video</span
                >
              {/if}
              {#if analytics.current_stats.is_sidecar}
                <span
                  class="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs"
                  >Carousel</span
                >
              {/if}
            </div>
          </div>
        </div>

        <!-- Key Metrics Cards -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div class="bg-red-50 rounded-lg p-4 border border-red-200">
            <div class="flex items-center">
              <div class="bg-red-500 rounded-full p-2 mr-3">
                <svg
                  class="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                  />
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-red-800">Total Likes</p>
                <p class="text-2xl font-bold text-red-900">
                  {formatNumber(analytics.current_stats.likes_count)}
                </p>
                {#if analytics.growth_metrics.total_likes_growth}
                  <p class="text-xs text-red-700">
                    +{formatNumber(analytics.growth_metrics.total_likes_growth)}
                    growth
                  </p>
                {/if}
              </div>
            </div>
          </div>

          <div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div class="flex items-center">
              <div class="bg-blue-500 rounded-full p-2 mr-3">
                <svg
                  class="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-blue-800">Total Comments</p>
                <p class="text-2xl font-bold text-blue-900">
                  {formatNumber(analytics.current_stats.comments_count)}
                </p>
                {#if analytics.growth_metrics.total_comments_growth}
                  <p class="text-xs text-blue-700">
                    +{formatNumber(
                      analytics.growth_metrics.total_comments_growth
                    )} growth
                  </p>
                {/if}
              </div>
            </div>
          </div>

          <div class="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <div class="flex items-center">
              <div class="bg-purple-500 rounded-full p-2 mr-3">
                <svg
                  class="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                  <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-purple-800">
                  Engagement Score
                </p>
                <p class="text-2xl font-bold text-purple-900">
                  {analytics.current_stats.engagement_score || 0}%
                </p>
                <p class="text-xs text-purple-700">
                  {analytics.engagement_breakdown.engagement_rate}% rate
                </p>
              </div>
            </div>
          </div>

          <div class="bg-green-50 rounded-lg p-4 border border-green-200">
            <div class="flex items-center">
              <div class="bg-green-500 rounded-full p-2 mr-3">
                <svg
                  class="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-green-800">Growth Rate</p>
                <p class="text-lg font-bold text-green-900">
                  {analytics.growth_metrics.likes_per_hour || 0}/hr
                </p>
                <p class="text-xs text-green-700">Average likes per hour</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Timeline Chart -->
        {#if analytics.timeline && analytics.timeline.length > 1}
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h4 class="text-lg font-semibold text-gray-900 mb-4">
              Engagement Over Time
            </h4>
            <div class="h-80">
              <canvas id="analyticsChart"></canvas>
            </div>
          </div>
        {/if}

        <!-- Insights -->
        {#if analytics.performance_insights && analytics.performance_insights.length > 0}
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h4 class="text-lg font-semibold text-gray-900 mb-4">
              Performance Insights
            </h4>
            <div class="space-y-3">
              {#each analytics.performance_insights as insight}
                <div
                  class="flex items-start space-x-3 p-3 rounded-lg {insight.type ===
                  'positive'
                    ? 'bg-green-50 border border-green-200'
                    : insight.type === 'warning'
                      ? 'bg-yellow-50 border border-yellow-200'
                      : insight.type === 'info'
                        ? 'bg-blue-50 border border-blue-200'
                        : 'bg-gray-50 border border-gray-200'}"
                >
                  <div
                    class="rounded-full p-1 {insight.type === 'positive'
                      ? 'bg-green-500'
                      : insight.type === 'warning'
                        ? 'bg-yellow-500'
                        : insight.type === 'info'
                          ? 'bg-blue-500'
                          : 'bg-gray-500'}"
                  >
                    <svg
                      class="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      {#if insight.type === "positive"}
                        <path
                          fill-rule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clip-rule="evenodd"
                        />
                      {:else if insight.type === "warning"}
                        <path
                          fill-rule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clip-rule="evenodd"
                        />
                      {:else}
                        <path
                          fill-rule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clip-rule="evenodd"
                        />
                      {/if}
                    </svg>
                  </div>
                  <div>
                    <h5 class="font-medium text-gray-900">{insight.title}</h5>
                    <p class="text-sm text-gray-600">{insight.description}</p>
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/if}

        <!-- Content Analysis -->
        {#if analytics.content_analysis}
          {@const content = analytics.content_analysis}
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h4 class="text-lg font-semibold text-gray-900 mb-4">
              🎯 Content Analysis
            </h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Caption Metrics -->
              <div class="bg-purple-50 rounded-lg p-4">
                <h5 class="font-medium text-purple-900 mb-3">
                  📝 Caption Analysis
                </h5>
                <div class="space-y-2">
                  <div class="flex justify-between">
                    <span class="text-sm text-purple-700">Words:</span>
                    <span class="font-medium text-purple-900"
                      >{content.caption_metrics.word_count}</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-purple-700">Hashtags:</span>
                    <span class="font-medium text-purple-900"
                      >{content.caption_metrics.hashtag_count}</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-purple-700">Mentions:</span>
                    <span class="font-medium text-purple-900"
                      >{content.caption_metrics.mention_count}</span
                    >
                  </div>
                  {#if content.caption_metrics.hashtags.length > 0}
                    <div class="mt-2">
                      <span class="text-xs text-purple-600">Top hashtags:</span>
                      <div class="flex flex-wrap gap-1 mt-1">
                        {#each content.caption_metrics.hashtags.slice(0, 5) as hashtag}
                          <span
                            class="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded"
                            >{hashtag}</span
                          >
                        {/each}
                      </div>
                    </div>
                  {/if}
                </div>
              </div>

              <!-- Content Strategy -->
              <div class="bg-green-50 rounded-lg p-4">
                <h5 class="font-medium text-green-900 mb-3">
                  📊 Content Strategy
                </h5>
                <div class="space-y-2">
                  <div class="flex justify-between">
                    <span class="text-sm text-green-700">Type:</span>
                    <span class="font-medium text-green-900 capitalize"
                      >{content.content_type}</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-green-700">Posted:</span>
                    <span class="font-medium text-green-900"
                      >{content.posting_strategy.posting_day}
                      {content.posting_strategy.posting_hour}:00</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-green-700">Tagged Users:</span>
                    <span class="font-medium text-green-900"
                      >{content.engagement_factors.tagged_users_count}</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-green-700">Location:</span>
                    <span class="font-medium text-green-900"
                      >{content.engagement_factors.location_used
                        ? "Yes"
                        : "No"}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <!-- Audience Behavior Analysis -->
        {#if analytics.audience_behavior}
          {@const audience = analytics.audience_behavior}
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h4 class="text-lg font-semibold text-gray-900 mb-4">
              👥 Audience Behavior Analysis
            </h4>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <!-- Engagement Velocity -->
              <div class="bg-blue-50 rounded-lg p-4">
                <h5 class="font-medium text-blue-900 mb-3">
                  ⚡ Engagement Velocity
                </h5>
                <div class="space-y-2">
                  <div class="flex justify-between">
                    <span class="text-sm text-blue-700">Likes/hour:</span>
                    <span class="font-medium text-blue-900"
                      >{audience.engagement_velocity?.avg_likes_per_hour ||
                        0}</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-blue-700">Comments/hour:</span>
                    <span class="font-medium text-blue-900"
                      >{audience.engagement_velocity?.avg_comments_per_hour ||
                        0}</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-blue-700">Velocity Score:</span>
                    <span class="font-medium text-blue-900"
                      >{audience.engagement_velocity?.velocity_score ||
                        0}/100</span
                    >
                  </div>
                </div>
              </div>

              <!-- Audience Quality -->
              <div class="bg-green-50 rounded-lg p-4">
                <h5 class="font-medium text-green-900 mb-3">
                  🎯 Audience Quality
                </h5>
                <div class="space-y-2">
                  <div class="flex justify-between">
                    <span class="text-sm text-green-700">Comment Rate:</span>
                    <span class="font-medium text-green-900"
                      >{audience.audience_quality?.comment_like_ratio ||
                        0}%</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-green-700">Quality Score:</span>
                    <span class="font-medium text-green-900"
                      >{audience.audience_quality?.quality_score || 0}/100</span
                    >
                  </div>
                  <div class="text-xs text-green-600 mt-2">
                    {audience.audience_quality?.quality_tier || "Unknown"}
                  </div>
                </div>
              </div>

              <!-- Social Signals -->
              <div class="bg-purple-50 rounded-lg p-4">
                <h5 class="font-medium text-purple-900 mb-3">
                  🚀 Social Signals
                </h5>
                <div class="space-y-2">
                  <div class="flex justify-between">
                    <span class="text-sm text-purple-700">Viral Potential:</span
                    >
                    <span class="font-medium text-purple-900"
                      >{audience.social_signals?.viral_potential || 0}/100</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-purple-700">Shareability:</span>
                    <span class="font-medium text-purple-900"
                      >{audience.social_signals?.shareability_score ||
                        0}/100</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-purple-700">Influence:</span>
                    <span class="font-medium text-purple-900"
                      >{audience.social_signals?.influence_score || 0}/100</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <!-- Optimization Suggestions -->
        {#if analytics.optimization_suggestions && analytics.optimization_suggestions.length > 0}
          <div class="bg-yellow-50 rounded-lg border border-yellow-200 p-6">
            <h4 class="text-lg font-semibold text-gray-900 mb-4">
              💡 Optimization Suggestions
            </h4>
            <div class="space-y-4">
              {#each analytics.optimization_suggestions as suggestion}
                <div
                  class="flex items-start space-x-3 p-4 bg-white rounded-lg border border-yellow-200"
                >
                  <div
                    class="rounded-full p-2 {suggestion.priority === 'high'
                      ? 'bg-red-500'
                      : suggestion.priority === 'medium'
                        ? 'bg-yellow-500'
                        : 'bg-green-500'}"
                  >
                    <svg
                      class="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center justify-between mb-2">
                      <h5 class="font-medium text-gray-900 capitalize">
                        {suggestion.type}
                      </h5>
                      <span
                        class="text-xs px-2 py-1 rounded-full {suggestion.priority ===
                        'high'
                          ? 'bg-red-100 text-red-800'
                          : suggestion.priority === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'}"
                      >
                        {suggestion.priority} priority
                      </span>
                    </div>
                    <p class="text-sm text-gray-700 mb-2">
                      {suggestion.suggestion}
                    </p>
                    <p class="text-xs text-green-600 font-medium">
                      💰 {suggestion.impact}
                    </p>
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      {/if}
    </div>
  {/snippet}
</Modal>
